import { LocalRepository } from '@lib/api/repository/local';
import {
  LocalCartInput,
  LocalCredential,
  LocalItemInput,
  LocalOfferFilters,
  LocalSessionInput,
  LocalSessionUpdateInput,
} from '@lib/types/local';

import { Requester } from '../clients';
import { toCart, toOffer, toOrder, toSession } from './mapper';
import {
  LocalCartDTO,
  LocalCredentialDTO,
  LocalFilterOptionsDTO,
  LocalOfferDTO,
  LocalOrderDTO,
  LocalSessionDTO,
} from './types';

const cartPath = (input: LocalCartInput) =>
  `/local/sessions/${input.sessionId}/carts/${input.id}`;
const headers = (input: LocalCredential) => ({ 'x-local-token': input.token });
export default class LocalAPI implements LocalRepository {
  async sessions(signal?: AbortSignal) {
    const { data } = await Requester.get<LocalSessionDTO[]>(
      '/local/owner/sessions',
      { signal },
    );
    return data.map((item) => toSession(item));
  }

  async sessionBySlug(slug: string, signal?: AbortSignal) {
    const { data } = await Requester.get<LocalSessionDTO | null>(
      `/local/sessions/store/${encodeURIComponent(slug)}`,
      { signal },
    );
    return data ? toSession(data) : null;
  }

  async session(id: string, signal?: AbortSignal) {
    const { data } = await Requester.get<LocalSessionDTO>(
      `/local/sessions/${id}`,
      { signal },
    );
    return toSession(data);
  }

  async offers(
    id: string,
    search: string,
    page: number,
    signal?: AbortSignal,
    filters: LocalOfferFilters = {},
  ) {
    const { data } = await Requester.get<{
      items: LocalOfferDTO[];
      hasMore: boolean;
      total: number;
    }>(`/local/sessions/${id}/offers`, {
      params: {
        search,
        page,
        collectionId: filters.collectionId,
        rarityId: filters.rarityId,
      },
      signal,
    });
    return {
      hasMore: data.hasMore,
      total: data.total,
      items: data.items.map((item) => toOffer(item)),
    };
  }

  async ownerOrders(signal?: AbortSignal) {
    const { data } = await Requester.get<LocalOrderDTO[]>('/local/owner/orders', { signal });
    return data.map((item) => toOrder(item));
  }

  async orders(id: string, signal?: AbortSignal) {
    const { data } = await Requester.get<LocalOrderDTO[]>(
      `/local/owner/sessions/${id}/orders`,
      { signal },
    );
    return data.map((item) => toOrder(item));
  }

  async cart(input: LocalCartInput, signal?: AbortSignal) {
    const { data } = await Requester.get<LocalCartDTO>(cartPath(input), {
      headers: headers(input),
      signal,
    });
    return toCart(data);
  }

  async filters(id: string, signal?: AbortSignal) {
    const { data } = await Requester.get<LocalFilterOptionsDTO>(
      `/local/sessions/${id}/filters`,
      { signal },
    );

    return {
      collections: data.collections.map(({ id, name }) => ({ id, name })),
      rarities: data.rarities.map(({ id, name }) => ({ id, name })),
    };
  }

  async updateSession(input: LocalSessionUpdateInput) {
    const { data } = await Requester.patch<LocalSessionDTO>(
      `/local/owner/sessions/${input.id}`,
      { body: { expiresAt: input.expiresAt } },
    );

    return toSession(data);
  }

  async createSession(input?: LocalSessionInput) {
    const { data } = await Requester.post<LocalSessionDTO>(
      '/local/owner/sessions',
      { body: input ? { expiresAt: input.expiresAt } : undefined },
    );
    return toSession(data);
  }

  async closeSession(id: string) {
    await Requester.post(`/local/owner/sessions/${id}/close`);
  }

  async createCart(sessionId: string) {
    const { data } = await Requester.post<LocalCredentialDTO>(
      `/local/sessions/${sessionId}/carts`,
    );
    return { id: data.id, token: data.token };
  }

  async updateCart(input: LocalItemInput) {
    await Requester.patch(`${cartPath(input)}/items`, {
      headers: headers(input),
      body: { offerId: input.offerId, quantity: input.quantity },
    });
  }

  async submit(input: LocalCartInput) {
    const { data } = await Requester.post<LocalOrderDTO>(
      `${cartPath(input)}/submit`,
      { headers: headers(input) },
    );
    return toOrder(data);
  }

  async approve(id: string) {
    await Requester.post(`/local/owner/orders/${id}/approve`);
  }

  async reject(id: string) {
    await Requester.post(`/local/owner/orders/${id}/reject`);
  }
}
